import { ChannelIcon, Empty, ErrorState, Loading, PageHead, useFormat } from '@ly360/ui';
import { useQuery } from '@tanstack/react-query';
import { Cake, CalendarHeart, MessageCircleQuestion, PackageX, RefreshCw, UserMinus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { NewCampaignDialog, type CampaignPreset } from './CampaignForm';
import { roas, type Rule } from './data';
import { BarList, ClassBadge, DailyChart, PlatformLabel, SourceLabel, Tabs, Trend } from './ui';

interface Overview {
  days: number;
  totals: {
    revenueMinor: string;
    orders: number;
    customers: number;
    prevRevenueMinor: string;
    prevOrders: number;
    campaignRevenueMinor: string;
    chatRevenueMinor: string;
  };
  bySource: { source: string; orders: number; revenueMinor: string }[];
  byStore: { id: string; name: string; nameAr: string | null; orders: number; revenueMinor: string }[];
  series: { day: string; revenueMinor: string; orders: number }[];
  conversion: { channel: string; conversations: number; converted: number }[];
  ads: {
    platform: string;
    spendMinor: string;
    revenueMinor: string;
    orders: number;
    conversations: number;
  }[];
  topProducts: { id: string; brand: string; name: string; units: number; revenueMinor: string }[];
  classes: { class: string; people: number }[];
}
interface Opportunities {
  replenish: {
    personId: string;
    personName: string;
    personNameAr: string | null;
    productId: string;
    brand: string;
    name: string;
    sizeMl: number | null;
    stock: number | null;
    boughtAt: string;
    dueOn: string;
    valueMinor: string;
  }[];
  waitlist: {
    productId: string;
    brand: string;
    name: string;
    stock: number | null;
    waiting: number;
    valueMinor: string;
  }[];
  drifting: {
    personId: string;
    personName: string;
    personNameAr: string | null;
    class: string;
    orders: number;
    spentMinor: string;
    lastOrderAt: string;
  }[];
  enquiries: {
    conversationId: string;
    channel: string;
    personId: string;
    personName: string;
    text: string | null;
    at: string;
    assigneeName: string | null;
  }[];
  birthdays: {
    personId: string;
    personName: string;
    personNameAr: string | null;
    day: string;
    class: string;
  }[];
  occasions: {
    key: string;
    date: string;
    startCampaignBy: string;
    daysAway: number;
    campaign: { id: string; name: string; status: string } | null;
  }[];
}

type OppTab = 'replenish' | 'waitlist' | 'drifting' | 'enquiries' | 'birthdays' | 'occasions';
const CLASS_ORDER = ['vip', 'loyal', 'new', 'one_time', 'at_risk', 'lapsed', 'prospect'];

export function GrowthPage() {
  const { t, i18n } = useTranslation();
  const fmt = useFormat();
  const { session, can } = useAuth();
  const currency = session!.tenant.currency;
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState<OppTab>('replenish');
  const [preset, setPreset] = useState<CampaignPreset | null>(null);
  const [showAll, setShowAll] = useState(false);
  const limit = <T,>(xs: T[]) => (showAll ? xs : xs.slice(0, 8));
  const ar = i18n.language === 'ar';
  const money = (minor: number | string) => fmt.money(Number(minor), currency);
  const overview = useQuery({
    queryKey: ['growth', 'overview', days],
    queryFn: () => api<Overview>(`/v1/growth/overview?days=${days}`),
  });
  const opp = useQuery({
    queryKey: ['growth', 'opportunities'],
    queryFn: () => api<Opportunities>('/v1/growth/opportunities'),
  });
  const canCampaign = can('campaigns:manage');
  const campaignButton = (p: CampaignPreset, label = t('retail.growth.startCampaign')) =>
    canCampaign && (
      <button className="btn btn-sm btn-grad" onClick={() => setPreset(p)} data-testid="opp-campaign">
        {label}
      </button>
    );
  const segmentPreset = (
    name: string,
    rules: Rule[],
    extra: Partial<CampaignPreset> = {},
  ): CampaignPreset => ({
    name,
    segmentName: name,
    rules,
    ...extra,
  });

  if (overview.isLoading) return <Loading />;
  if (overview.isError || !overview.data)
    return <ErrorState error={overview.error} onRetry={() => void overview.refetch()} />;
  const o = overview.data;
  const revenue = Number(o.totals.revenueMinor);
  const share = (part: string) => (revenue ? Math.round((Number(part) / revenue) * 100) : 0);
  const classes = Object.fromEntries(o.classes.map((c) => [c.class, c.people]));
  const op = opp.data;

  return (
    <>
      <PageHead
        eyebrow={t('nav.sales')}
        title={t('retail.growth.title')}
        subtitle={t('retail.growth.subtitle')}
        actions={
          <div className="segmented" role="group" aria-label={t('retail.growth.period')}>
            {[30, 90, 365].map((d) => (
              <button key={d} type="button" aria-pressed={days === d} onClick={() => setDays(d)}>
                {t('retail.growth.lastDays', { count: d })}
              </button>
            ))}
          </div>
        }
      />
      <div className="stat-grid" style={{ marginBottom: '1rem' }} data-testid="growth-stats">
        <div className="stat">
          <div className="label">{t('retail.growth.revenue')}</div>
          <div className="value">{money(o.totals.revenueMinor)}</div>
          <div className="stat-note">
            <Trend now={revenue} before={Number(o.totals.prevRevenueMinor)} />
          </div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.growth.orders')}</div>
          <div className="value">{fmt.number(o.totals.orders)}</div>
          <div className="stat-note">
            <Trend now={o.totals.orders} before={o.totals.prevOrders} />
          </div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.growth.averageOrder')}</div>
          <div className="value">{money(o.totals.orders ? Math.round(revenue / o.totals.orders) : 0)}</div>
          <div className="stat-note faint">{t('retail.growth.customers', { count: o.totals.customers })}</div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.growth.fromChats')}</div>
          <div className="value">{fmt.number(share(o.totals.chatRevenueMinor))}%</div>
          <div className="stat-note faint">{money(o.totals.chatRevenueMinor)}</div>
        </div>
        <div className="stat">
          <div className="label">{t('retail.growth.fromCampaigns')}</div>
          <div className="value">{fmt.number(share(o.totals.campaignRevenueMinor))}%</div>
          <div className="stat-note faint">{money(o.totals.campaignRevenueMinor)}</div>
        </div>
      </div>

      <section className="card" style={{ marginBottom: '1rem' }} data-testid="opportunities">
        <div className="card-head">
          <div>
            <h2>{t('retail.growth.opportunities')}</h2>
            <p className="faint card-sub">{t('retail.growth.opportunitiesHint')}</p>
          </div>
        </div>
        <div className="card-pad" style={{ paddingTop: 0 }}>
          <Tabs<OppTab>
            label={t('retail.growth.opportunities')}
            value={tab}
            onChange={(v) => {
              setTab(v);
              setShowAll(false);
            }}
            tabs={[
              { key: 'replenish', label: t('retail.opp.replenish'), count: op?.replenish.length },
              {
                key: 'waitlist',
                label: t('retail.opp.waitlist'),
                count: op?.waitlist.reduce((s, w) => s + w.waiting, 0),
              },
              { key: 'drifting', label: t('retail.opp.drifting'), count: op?.drifting.length },
              { key: 'enquiries', label: t('retail.opp.enquiries'), count: op?.enquiries.length },
              { key: 'birthdays', label: t('retail.opp.birthdays'), count: op?.birthdays.length },
              { key: 'occasions', label: t('retail.opp.occasions'), count: op?.occasions.length },
            ]}
          />
          {opp.isLoading && <Loading />}
          {opp.isError && <ErrorState error={opp.error} />}
          {op && tab === 'replenish' && (
            <OppSection
              icon={<RefreshCw size={18} />}
              text={t('retail.opp.replenishHint')}
              action={campaignButton(
                segmentPreset(t('retail.opp.replenishCampaign'), [{ type: 'replenishDue' }], {
                  templateName: 'back_in_stock',
                }),
              )}
              empty={!op.replenish.length}
            >
              <table className="table table-responsive" data-testid="opp-replenish">
                <thead>
                  <tr>
                    <th>{t('retail.col.customer')}</th>
                    <th>{t('retail.col.product')}</th>
                    <th>{t('retail.col.bought')}</th>
                    <th>{t('retail.col.runsOut')}</th>
                    <th className="num">{t('retail.col.value')}</th>
                  </tr>
                </thead>
                <tbody>
                  {limit(op.replenish).map((r) => (
                    <tr key={`${r.personId}-${r.productId}`}>
                      <td data-label={t('retail.col.customer')}>
                        <Link to={`/people/${r.personId}`} dir="auto">
                          {(ar && r.personNameAr) || r.personName}
                        </Link>
                      </td>
                      <td data-label={t('retail.col.product')} dir="auto">
                        {r.brand} {r.name}
                        {r.sizeMl && <span className="faint"> · {r.sizeMl} ml</span>}
                      </td>
                      <td data-label={t('retail.col.bought')}>{fmt.date(r.boughtAt)}</td>
                      <td data-label={t('retail.col.runsOut')}>
                        <DueLabel dueOn={r.dueOn} />
                      </td>
                      <td data-label={t('retail.col.value')} className="num">
                        {money(r.valueMinor)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </OppSection>
          )}
          {op && tab === 'waitlist' && (
            <OppSection
              icon={<PackageX size={18} />}
              text={t('retail.opp.waitlistHint')}
              empty={!op.waitlist.length}
            >
              <ul className="opp-list" data-testid="opp-waitlist">
                {op.waitlist.map((w) => (
                  <li key={w.productId}>
                    <span className="grow" dir="auto">
                      <strong>
                        {w.brand} {w.name}
                      </strong>
                      <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                        {t('retail.opp.waiting', { count: w.waiting })} ·{' '}
                        {t('retail.opp.potential', { value: money(w.valueMinor) })}
                      </span>
                    </span>
                    {w.stock ? (
                      campaignButton(
                        segmentPreset(
                          t('retail.opp.backInStockName', { product: `${w.brand} ${w.name}` }),
                          [{ type: 'waitlist', productIds: [w.productId] }],
                          { templateName: 'back_in_stock' },
                        ),
                        t('retail.opp.notify', { count: w.waiting }),
                      )
                    ) : (
                      <span className="badge badge-warning">{t('retail.stock.out')}</span>
                    )}
                  </li>
                ))}
              </ul>
            </OppSection>
          )}
          {op && tab === 'drifting' && (
            <OppSection
              icon={<UserMinus size={18} />}
              text={t('retail.opp.driftingHint')}
              action={campaignButton(
                segmentPreset(t('retail.opp.winBackName'), [
                  { type: 'class', classes: ['at_risk', 'lapsed'] },
                  { type: 'orders', min: 2 },
                ]),
              )}
              empty={!op.drifting.length}
            >
              <table className="table table-responsive" data-testid="opp-drifting">
                <thead>
                  <tr>
                    <th>{t('retail.col.customer')}</th>
                    <th>{t('retail.col.class')}</th>
                    <th className="num">{t('retail.col.orders')}</th>
                    <th className="num">{t('retail.col.spent')}</th>
                    <th>{t('retail.col.lastOrder')}</th>
                  </tr>
                </thead>
                <tbody>
                  {limit(op.drifting).map((d) => (
                    <tr key={d.personId}>
                      <td data-label={t('retail.col.customer')}>
                        <Link to={`/people/${d.personId}`} dir="auto">
                          {(ar && d.personNameAr) || d.personName}
                        </Link>
                      </td>
                      <td data-label={t('retail.col.class')}>
                        <ClassBadge value={d.class} />
                      </td>
                      <td data-label={t('retail.col.orders')} className="num">
                        {fmt.number(d.orders)}
                      </td>
                      <td data-label={t('retail.col.spent')} className="num">
                        {money(d.spentMinor)}
                      </td>
                      <td data-label={t('retail.col.lastOrder')}>{fmt.relative(d.lastOrderAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </OppSection>
          )}
          {op && tab === 'enquiries' && (
            <OppSection
              icon={<MessageCircleQuestion size={18} />}
              text={t('retail.opp.enquiriesHint')}
              empty={!op.enquiries.length}
            >
              <ul className="opp-list" data-testid="opp-enquiries">
                {limit(op.enquiries).map((e) => (
                  <li key={e.conversationId}>
                    <ChannelIcon channel={e.channel} size={14} />
                    <span className="grow">
                      <strong dir="auto">{e.personName}</strong>
                      <span className="faint"> · {fmt.relative(e.at)}</span>
                      <span className="opp-quote" dir="auto">
                        “{e.text}”
                      </span>
                      <span className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                        {e.assigneeName
                          ? t('retail.opp.handledBy', { name: e.assigneeName })
                          : t('retail.opp.unassigned')}
                      </span>
                    </span>
                    <Link className="btn btn-sm" to={`/inbox?c=${e.conversationId}`}>
                      {t('retail.opp.openChat')}
                    </Link>
                  </li>
                ))}
              </ul>
            </OppSection>
          )}
          {op && tab === 'birthdays' && (
            <OppSection
              icon={<Cake size={18} />}
              text={t('retail.opp.birthdaysHint')}
              action={campaignButton(
                segmentPreset(t('retail.opp.birthdayName'), [{ type: 'birthday', withinDays: 14 }]),
              )}
              empty={!op.birthdays.length}
            >
              <ul className="opp-list" data-testid="opp-birthdays">
                {limit(op.birthdays).map((b) => (
                  <li key={b.personId}>
                    <Cake size={14} className="faint" />
                    <Link className="grow" to={`/people/${b.personId}`} dir="auto">
                      {(ar && b.personNameAr) || b.personName}
                    </Link>
                    <ClassBadge value={b.class} />
                    <span className="faint">{fmt.dayMonth(`2000-${b.day}T12:00:00Z`)}</span>
                  </li>
                ))}
              </ul>
            </OppSection>
          )}
          {op && tab === 'occasions' && (
            <OppSection
              icon={<CalendarHeart size={18} />}
              text={t('retail.opp.occasionsHint')}
              empty={!op.occasions.length}
            >
              <ul className="opp-list" data-testid="opp-occasions">
                {op.occasions.map((oc) => (
                  <li key={`${oc.key}-${oc.date}`}>
                    <span className="grow">
                      <strong>{t(`retail.occasion.${oc.key}`)}</strong>
                      <span className="faint"> · {fmt.date(`${oc.date}T12:00:00Z`)}</span>
                      <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
                        {t('retail.opp.daysAway', { count: oc.daysAway })} ·{' '}
                        {t('retail.opp.startBy', { date: fmt.date(`${oc.startCampaignBy}T12:00:00Z`) })}
                      </span>
                    </span>
                    {oc.campaign ? (
                      <Link className="btn btn-sm" to={`/campaigns/${oc.campaign.id}`}>
                        {t(`retail.campaignStatus.${oc.campaign.status}`)}: {oc.campaign.name}
                      </Link>
                    ) : (
                      campaignButton(
                        {
                          name: `${t(`retail.occasion.${oc.key}`)} ${oc.date.slice(0, 4)}`,
                          occasion: oc.key,
                          startsAt: `${oc.startCampaignBy}T09:00`,
                        },
                        t('retail.opp.plan'),
                      )
                    )}
                  </li>
                ))}
              </ul>
            </OppSection>
          )}
          {op && !showAll && (op[tab] as unknown[]).length > 8 && (
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAll(true)} data-testid="show-all">
              {t('retail.opp.showAll', { count: (op[tab] as unknown[]).length })}
            </button>
          )}
        </div>
      </section>

      <div className="growth-grid">
        <section className="card card-pad">
          <h2 className="section-title">{t('retail.growth.daily')}</h2>
          <DailyChart series={o.series} currency={currency} label={t('retail.growth.daily')} />
        </section>
        <section className="card card-pad">
          <h2 className="section-title">{t('retail.growth.bySource')}</h2>
          <BarList
            testId="by-source"
            rows={o.bySource.map((s) => ({
              key: s.source,
              label: <SourceLabel source={s.source} />,
              value: Number(s.revenueMinor),
              display: money(s.revenueMinor),
              note: t('retail.growth.ordersCount', { count: s.orders }),
            }))}
          />
        </section>
        <section className="card card-pad">
          <h2 className="section-title">{t('retail.growth.ads')}</h2>
          {o.ads.length ? (
            <div className="table-wrap">
              <table className="table" data-testid="ads-summary">
                <thead>
                  <tr>
                    <th>{t('retail.col.platform')}</th>
                    <th className="num">{t('retail.col.spend')}</th>
                    <th className="num">{t('retail.col.revenue')}</th>
                    <th className="num">{t('retail.col.roas')}</th>
                  </tr>
                </thead>
                <tbody>
                  {o.ads.map((a) => {
                    const r = roas(Number(a.revenueMinor), Number(a.spendMinor));
                    return (
                      <tr key={a.platform}>
                        <td>
                          <PlatformLabel platform={a.platform} short />
                        </td>
                        <td className="num">{money(a.spendMinor)}</td>
                        <td className="num">{money(a.revenueMinor)}</td>
                        <td className={`num ${r !== null && r < 1 ? 'trend-down' : 'trend-up'}`}>
                          {r === null ? '—' : `${fmt.number(Math.round(r * 10) / 10)}×`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <Empty text={t('retail.growth.noAds')} />
          )}
          <Link to="/campaigns" className="btn btn-ghost btn-sm" style={{ marginTop: '0.5rem' }}>
            {t('nav.campaigns')}
          </Link>
        </section>
        <section className="card card-pad">
          <h2 className="section-title">{t('retail.growth.chatConversion')}</h2>
          <p className="faint card-sub">{t('retail.growth.chatConversionHint')}</p>
          <BarList
            testId="chat-conversion"
            rows={o.conversion.map((c) => ({
              key: c.channel,
              label: (
                <span className="channel-badge">
                  <ChannelIcon channel={c.channel} size={12} />
                  <span>{t(`channels.${c.channel}`)}</span>
                </span>
              ),
              value: c.conversations ? c.converted / c.conversations : 0,
              display: `${fmt.number(c.conversations ? Math.round((c.converted / c.conversations) * 100) : 0)}%`,
              note: t('retail.growth.converted', { converted: c.converted, total: c.conversations }),
            }))}
          />
        </section>
        <section className="card card-pad">
          <h2 className="section-title">{t('retail.growth.customersByClass')}</h2>
          <BarList
            testId="by-class"
            rows={CLASS_ORDER.filter((c) => classes[c]).map((c) => ({
              key: c,
              label: <ClassBadge value={c} />,
              value: classes[c]!,
              display: fmt.number(classes[c]!),
              note: t(`retail.classHint.${c}`),
            }))}
          />
        </section>
        <section className="card card-pad">
          <h2 className="section-title">{t('retail.growth.byStore')}</h2>
          <BarList
            rows={o.byStore
              .filter((s) => Number(s.revenueMinor) > 0)
              .map((s) => ({
                key: s.id,
                label: <span dir="auto">{(ar && s.nameAr) || s.name}</span>,
                value: Number(s.revenueMinor),
                display: money(s.revenueMinor),
              }))}
          />
        </section>
        <section className="card card-pad">
          <h2 className="section-title">{t('retail.growth.topProducts')}</h2>
          <BarList
            rows={o.topProducts.map((p) => ({
              key: p.id,
              label: (
                <span dir="auto">
                  {p.brand} {p.name}
                </span>
              ),
              value: Number(p.revenueMinor),
              display: money(p.revenueMinor),
              note: t('retail.growth.units', { count: p.units }),
            }))}
          />
        </section>
      </div>
      {preset && <NewCampaignDialog open preset={preset} onClose={() => setPreset(null)} />}
    </>
  );
}

function OppSection({
  icon,
  text,
  action,
  empty,
  children,
}: {
  icon: React.ReactNode;
  text: string;
  action?: React.ReactNode;
  empty: boolean;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <div className="opp-section">
      <div className="opp-intro">
        <span className="opp-icon">{icon}</span>
        <p className="grow">{text}</p>
        {!empty && action}
      </div>
      {empty ? <Empty text={t('retail.opp.none')} /> : <div className="table-wrap">{children}</div>}
    </div>
  );
}

function DueLabel({ dueOn }: { dueOn: string }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const days = Math.round((new Date(`${dueOn}T12:00:00Z`).getTime() - Date.now()) / 86_400_000);
  return (
    <span className={days < 0 ? 'overdue' : ''}>
      {days < 0 ? t('retail.opp.ranOut', { count: -days }) : t('retail.opp.runsOutIn', { count: days })}
      <span className="faint" style={{ display: 'block', fontSize: 'var(--t-xs)' }}>
        {fmt.date(`${dueOn}T12:00:00Z`)}
      </span>
    </span>
  );
}
